/**
 * <ActivationComposerPage> — le composeur de la reprise (page).
 *
 * L'écran où le parent choisit sa suite : classe, volume, matières, répartition, créneaux, date,
 * formule, engagement, puis le prix — UN seul formulaire, UN seul envoi (`ActivationComposerScreen.js`).
 *
 * Deux portes, une surface : le lien signé du mail et le portail connecté. La page ne sait pas
 * laquelle l'a ouverte — le titre et le libellé d'envoi suivent `editing`, et rien d'autre ne
 * change.
 *
 * Elle ne possède AUCUN état métier : l'envoi en cours, l'erreur du back et l'accusé de succès
 * arrivent en props, comme la saisie. Le geste de retour, la barre d'envoi et les deux accusés sont
 * tout ce que la page ajoute au corps.
 *
 * Le bouton d'envoi reste sous le pouce : la barre est FIXE en bas, le formulaire défile dessous,
 * comme le checkout du funnel.
 */
import React from "react"

import { ACTIVATION_CONTENT_WIDTH } from "../ActivationLandingPage"
import {
    ActivationComposerTemplate,
    type ActivationComposerTemplateProps,
} from "../../templates/ActivationComposerTemplate"
import { Button } from "../../atoms/Buttons/Button"
import { Header } from "../../molecules/Header"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { PageTemplate } from "../../templates/PageTemplate"
import { Snackbar } from "../../molecules/Snackbar"
import { ActivationComposerSkeleton } from "./ActivationComposerSkeleton"

export type ActivationComposerPageProps = ActivationComposerTemplateProps & {
    /**
     * Le parent modifie une reprise déjà posée. Le titre devient « Modifier la reprise » et l'envoi
     * « Enregistrer » — et non « Enregistrer les modifications » : on peut valider sans avoir rien
     * changé.
     */
    editing?: boolean

    /** L'envoi. Absent, ou offre non calculable (`summary` nul), le bouton ne répond pas. */
    onSubmit?: () => void
    submitting?: boolean

    /** Le refus du back, en clair — c'est lui qui porte la raison actionnable quand il en a une. */
    error?: string | null
    onDismissError?: () => void
    /** L'accusé de succès — « La suite est planifiée. » / « Modifications enregistrées. » */
    success?: string | null
    onDismissSuccess?: () => void

    /** Le retour — absent, la page n'a pas de flèche. */
    onBack?: () => void

    /** Le contexte du lien n'est pas encore arrivé : la page montre sa forme, comme l'arrivée. */
    loading?: boolean
}

export function ActivationComposerPage(props: ActivationComposerPageProps) {
    const {
        editing = false,
        onSubmit,
        submitting = false,
        error,
        onDismissError,
        success,
        onDismissSuccess,
        onBack,
        loading = false,
        testID,
        ...templateProps
    } = props

    // Offre non calculable : jamais 0 €, et jamais d'envoi — la garde du legacy, passée en prop.
    const canSubmit = onSubmit != null && templateProps.summary != null && !submitting

    return (
        <PageTemplate
            testID={testID}
            loading={loading}
            skeleton={<ActivationComposerSkeleton />}
            padding="4"
            maxContentWidth={ACTIVATION_CONTENT_WIDTH}
            footer={
                <Button
                    label={editing ? "Enregistrer" : "Activer la suite"}
                    fullWidth
                    loading={submitting}
                    disabled={!canSubmit}
                    onPress={onSubmit}
                />
            }
            header={
                <Header
                    title={editing ? "Modifier la reprise" : "Choisir la suite"}
                    left={
                        onBack != null && (
                            <IconButton
                                iconName="chevron-left"
                                appearance="transparent"
                                iconColor="primary"
                                size={32}
                                onPress={onBack}
                                accessibilityLabel="Retour"
                            />
                        )
                    }
                />
            }
        >
            <ActivationComposerTemplate {...templateProps} />

            {onDismissSuccess != null && (
                <Snackbar
                    visible={success != null && success !== ""}
                    message={success ?? ""}
                    tone="success"
                    onDismiss={onDismissSuccess}
                />
            )}
            {onDismissError != null && (
                <Snackbar
                    visible={error != null && error !== ""}
                    message={error ?? ""}
                    tone="danger"
                    autoHideMs={5000}
                    onDismiss={onDismissError}
                />
            )}
        </PageTemplate>
    )
}
